/**
 * outreach-seed.ts — pure planner for the mktg_mirror_leads -> crm_outreach lift.
 *
 * No I/O, no env, no Supabase. scripts/seed-outreach.ts is the CLI shell around
 * it; psql does the reads and the write so no credential ever reaches disk.
 *
 * The load-bearing rule lives in `contactedUrls`, and it is required input
 * rather than an optional refinement. The mirror's own `outreach_status`
 * column is NOT proof that a message never went out: measured on prod
 * 2026-07-31, 182 of 1,879 rows marked 'pending' had already been contacted,
 * and in the two campaigns this first run covers it was 72 of 230 — 31%.
 * Seeding on the mirror's status alone would have queued a second first-touch
 * to people who already received one, which is the one error this queue exists
 * to make impossible.
 *
 * The planner carries TWO texts per person, because LinkedIn has two rungs: the
 * note attached to the invite (capped near 300 chars) and the message that can
 * only be sent once the invite is accepted. The first version carried only the
 * second, so 220 people were loaded with nothing to attach to the request and
 * their notes had to be written by hand outside the product.
 *
 * WHICH mirror column holds which rung is not knowable from the data and is not
 * guessed — see `MirrorTextLayout`. The first attempt at this feature assumed
 * `warm_opener` was the note; reading the bodies on prod showed it is the nudge
 * sent long after, while `custom_message` holds "…Would like to connect.". That
 * mapping backwards would have introduced strangers with a follow-up.
 *
 * Every text follows one provenance rule — mirror copy normally, override copy
 * in rebrand mode, never a mix — nothing is truncated to fit, and every note
 * must clear `screenConnectionNote` before it may be queued.
 */

import {
  isCanonicalLinkedInUrl,
  toEnricherUrl,
} from '../marketing/engine/shared/url.js';
import { screenConnectionNote } from './outreach-note-gate.js';

export type OutreachBrand = 'qualiteh' | 'agenticmotion' | 'msp_deliveryos';

export const OUTREACH_BRANDS: readonly OutreachBrand[] = [
  'qualiteh',
  'agenticmotion',
  'msp_deliveryos',
];

/**
 * LinkedIn's connection-request note cap. Declared here rather than in
 * `outreach.ts` because that module already imports from this one — putting it
 * the other way round would close an import cycle. `outreach.ts` re-exports it,
 * so the UI keeps its existing import site.
 *
 * The seed NEVER truncates to this. It is a reporting threshold: the dry-run
 * says how many notes exceed it so the operator learns that before the write,
 * not on row 24 of the ritual.
 */
export const CONNECTION_NOTE_CHAR_LIMIT = 300;

export type MirrorLeadRow = {
  readonly linkedin_url: string | null;
  readonly first_name: string | null;
  readonly last_name: string | null;
  readonly company_name: string | null;
  readonly job_title: string | null;
  readonly campaign_key: string | null;
  readonly custom_message: string | null;
  readonly follow_up_message: string | null;
  /**
   * NOT the connection note, despite the name and despite what
   * 20260731140000_crm_outreach_connection_ladder.sql says — measured on prod
   * 2026-07-31 by reading bodies. Where it appears it is the LAST rung: the
   * nudge sent after a message went unanswered ("Manfred, following up. If
   * system QM is working at scale, that is impressive"). Its presence is what
   * marks a row as three-step; its absence means custom_message holds the whole
   * first message and there is no invite note at all. See MirrorTextLayout.
   */
  readonly warm_opener: string | null;
};

export type OutreachSeedRow = {
  readonly organization_id: string;
  readonly brand: OutreachBrand;
  readonly dedupe_key: string;
  readonly linkedin_url: string;
  readonly first_name: string | null;
  readonly last_name: string | null;
  readonly company_name: string | null;
  readonly job_title: string | null;
  readonly source_campaign_key: string | null;
  readonly message_text: string;
  readonly message_origin: 'mirror_original' | 'recrafted';
  readonly followup_text: string | null;
  /**
   * The text to attach to the connection request, carried VERBATIM — never
   * truncated to the cap and never invented. null means the operator sends a
   * bare request, which is legitimate and common.
   */
  readonly connection_note: string | null;
  /**
   * Every seeded row starts at the bottom of the LinkedIn ladder. A message
   * cannot be sent to a non-connection, so 'ready' was never a real state —
   * see 20260731140000_crm_outreach_connection_ladder.sql.
   */
  readonly state: 'needs_connection';
};

/**
 * A message rewritten outside the product — for a rebrand, or for a new offer to
 * the same audience. Keyed by the ORIGINAL linkedin_url; canonicalisation happens
 * here so the override folds onto the same person as the mirror row does.
 */
export type RecraftedMessage = {
  readonly linkedin_url: string;
  readonly message_text: string;
  readonly followup_text: string | null;
  /**
   * Optional. In override mode the note follows the SAME provenance rule as the
   * message: it comes from here or it is null. It never falls back to the
   * mirror's warm_opener, because that copy speaks for the brand being replaced
   * — the exact accident `no_recrafted_message` exists to prevent, applied to
   * the one field that reaches the recipient before the message does.
   */
  readonly connection_note?: string | null;
};

/**
 * Which of the mirror's three text columns means what, for the campaign being
 * loaded. This is an EXPLICIT INPUT and never inferred, because the upstream
 * engine is not consistent — measured on prod 2026-07-31 by reading bodies, not
 * lengths:
 *
 *   'message_only'      ai-quality-software, akademija-mladen
 *                       custom_message is the whole first message (avg 744-1073
 *                       chars) and there is no invite note at all.
 *
 *   'note_then_message' quality-outsourcing, msp-growth-signal
 *                       custom_message  = "…Would like to connect."  (avg 89)
 *                       follow_up_message = "thanks for connecting…" (avg 440+)
 *                       warm_opener     = "following up…"            (the nudge)
 *
 * A third arrangement exists (obuke_training: all three texts are POST-connection,
 * warm_opener = "hvala na konekciji") and is deliberately NOT modelled — those
 * people do not belong at the bottom of a ladder they have already climbed.
 * Exclude that campaign rather than teaching this enum to guess.
 *
 * Getting this backwards sends "following up…" to a stranger as an invitation
 * and "Would like to connect" after they already connected.
 */
export type MirrorTextLayout = 'message_only' | 'note_then_message';

export const MIRROR_TEXT_LAYOUTS: readonly MirrorTextLayout[] = [
  'message_only',
  'note_then_message',
];

export type OutreachSeedInput = {
  readonly orgId: string;
  readonly brand: OutreachBrand;
  readonly mirror: readonly MirrorLeadRow[];
  /**
   * Defaults to 'message_only' — the layout the 220 already-queued rows were
   * loaded under. A default that preserves shipped behaviour is the only safe
   * one here.
   */
  readonly layout?: MirrorTextLayout;
  /** Canonicalised inside — pass raw os_conversation_threads.lead_linkedin_url. */
  readonly contactedUrls: readonly (string | null)[];
  /** Already in crm_outreach for this org+brand; pass dedupe_key verbatim. */
  readonly queuedKeys: readonly string[];
  /**
   * Optional. When present, ONLY people with an override are queued, and the
   * override text is used with message_origin='recrafted'. Everything else —
   * the contacted check, the suppression triple applied upstream in SQL, URL
   * canonicalisation, dedupe — is unchanged, which is the whole reason a
   * rebrand reuses this planner instead of getting its own loader.
   */
  readonly recrafted?: readonly RecraftedMessage[];
};

export type OutreachSeedPlan = {
  readonly rows: readonly OutreachSeedRow[];
  readonly stats: {
    readonly mirrorRows: number;
    readonly alreadyContacted: number;
    readonly alreadyQueued: number;
    readonly rejected: number;
    readonly toInsert: number;
    /** Of the rows being inserted, how many carry a connection note at all. */
    readonly withNote: number;
    /** Of those, how many fit inside CONNECTION_NOTE_CHAR_LIMIT as written. */
    readonly noteWithinCap: number;
    /**
     * And how many do not. These are still inserted verbatim; the count exists
     * so the operator sees the hand-trimming cost before committing, since
     * campaigns differ wildly (measured 2026-07-31: quality-outsourcing 0 of
     * 243 over the cap, obuke_training 44 of 67).
     */
    readonly noteOverCap: number;
    /**
     * Notes the language gate refused. Those rows are still queued — as bare
     * requests — so this is never a count of lost people, only of text that was
     * not fit to send.
     */
    readonly notesRejectedByGate: number;
  };
  readonly rejections: Readonly<Record<string, number>>;
  readonly samples: readonly string[];
  /**
   * Up to ten rows that actually carry a note, collected independently of
   * `samples` so the U1 read is guaranteed to have bodies to read whenever any
   * exist. Empty only when the run queues no notes at all.
   */
  readonly noteSamples: readonly string[];
  /** Up to 20 human-readable gate rejections, for the dry-run report. */
  readonly noteGateFindings: readonly string[];
};

/**
 * Canonical identity for one person. BOTH guards run and neither is redundant:
 * toEnricherUrl folds case, ex-YU diacritics and percent-encoding but happily
 * lower-cases a Sales Navigator link or an /in/ACoAA… URN; isCanonicalLinkedInUrl
 * is what rejects those. Returns '' when the URL cannot establish identity.
 */
export const canonicalPersonUrl = (url: string | null | undefined): string => {
  const canonical = toEnricherUrl(url ?? null);
  return isCanonicalLinkedInUrl(canonical) ? canonical : '';
};

export const planOutreachSeed = (input: OutreachSeedInput): OutreachSeedPlan => {
  const contacted = new Set(input.contactedUrls.map(canonicalPersonUrl).filter(Boolean));
  const queued = new Set(input.queuedKeys.filter((k) => k.length > 0));
  const seen = new Set<string>();

  // Override map, keyed canonically so it folds onto the same person the mirror
  // row does. Absent => plain seed from the mirror copy.
  const overrides = new Map<string, RecraftedMessage>();
  for (const r of input.recrafted ?? []) {
    const key = canonicalPersonUrl(r.linkedin_url);
    if (key.length > 0 && r.message_text.trim().length > 0) overrides.set(key, r);
  }
  const useOverrides = input.recrafted !== undefined;

  const layout: MirrorTextLayout = input.layout ?? 'message_only';

  const rows: OutreachSeedRow[] = [];
  const rejections: Record<string, number> = {};
  const samples: string[] = [];
  // F3: kept SEPARATE from `samples`. A single capped list is ordered by mirror
  // id, so the first ten insertable rows can all be bare requests while hundreds
  // of later rows carry notes — the operator would then read zero note bodies
  // and still be asked to authorise a write whose whole point is the notes.
  const noteSamples: string[] = [];
  let alreadyContacted = 0;
  let alreadyQueued = 0;
  let withNote = 0;
  let noteWithinCap = 0;
  let noteOverCap = 0;
  let notesRejectedByGate = 0;
  const noteGateFindings: string[] = [];

  const reject = (reason: string): void => {
    rejections[reason] = (rejections[reason] ?? 0) + 1;
  };

  for (const lead of input.mirror) {
    // Which column carries which rung is declared by the caller (--layout), but
    // WITHIN a three-step campaign only the annotated rows are three-step.
    // Measured on prod 2026-07-31, quality-outsourcing holds both populations:
    // 280 rows carry a warm_opener and put the invite in custom_message (avg 89
    // chars, "…Would like to connect."), while 233 rows carry no opener and put
    // the entire pitch in custom_message (avg 572, up to 828). Mapping the
    // second group as if it were the first turns a 645-character sales pitch
    // into a LinkedIn invite note.
    //
    // The presence of warm_opener is the discriminator, and it is structural
    // rather than a guess: it is the third rung, so a row that has one is a row
    // whose earlier rungs are filled in.
    const threeStep =
      layout === 'note_then_message' && (lead.warm_opener ?? '').trim().length > 0;

    const message = ((threeStep ? lead.follow_up_message : lead.custom_message) ?? '').trim();
    if (message.length === 0) {
      reject('no_message');
      continue;
    }

    const key = canonicalPersonUrl(lead.linkedin_url);
    if (key.length === 0) {
      reject('invalid_or_urn_linkedin_url');
      continue;
    }

    if (contacted.has(key)) {
      alreadyContacted += 1;
      continue;
    }
    if (queued.has(key)) {
      alreadyQueued += 1;
      continue;
    }
    if (seen.has(key)) {
      reject('duplicate_in_run');
      continue;
    }

    // In override mode a person without a rewritten message is skipped, not
    // silently queued with the old brand's copy — that is precisely the
    // accident a rebrand must not have.
    const override = overrides.get(key);
    if (useOverrides && override === undefined) {
      reject('no_recrafted_message');
      continue;
    }
    seen.add(key);

    const finalMessage = override ? override.message_text.trim() : message;
    // In 'note_then_message' the mirror's follow_up_message has already been
    // consumed as the message itself, so the follow-up slot takes warm_opener —
    // the nudge that comes after silence. Nothing is discarded and nothing is
    // reused twice.
    const mirrorFollowup = threeStep ? lead.warm_opener : lead.follow_up_message;
    const finalFollowup = override
      ? (override.followup_text ?? '').trim() || null
      : (mirrorFollowup ?? '').trim() || null;
    // Same provenance rule as the message above: override copy in override
    // mode, mirror copy otherwise, and never a mix of the two. A row that is
    // not three-step has no invite note upstream at all — it is a bare request,
    // which is the volume lane and not a defect.
    const candidateNote = override
      ? (override.connection_note ?? '').trim() || null
      : threeStep
        ? (lead.custom_message ?? '').trim() || null
        : null;

    // The language gate is a MANDATORY input, not a lint, and it applies to
    // hand-written override notes exactly as it does to mirror copy — the two
    // defects found in the 220 live notes were both hand-written. A note that
    // fails is dropped rather than queued: the person stays in the queue as a
    // bare request (the volume lane anyway), and text nobody vetted never
    // reaches a stranger. Dropping the note is strictly safer than dropping the
    // person.
    const gateFindings = candidateNote === null
      ? []
      : screenConnectionNote(candidateNote, lead.first_name);

    const finalNote = gateFindings.length > 0 ? null : candidateNote;

    if (gateFindings.length > 0) {
      notesRejectedByGate += 1;
      if (noteGateFindings.length < 20) {
        const who = `${lead.first_name ?? ''} ${lead.last_name ?? ''}`.trim() || key;
        noteGateFindings.push(
          `${who}: ${gateFindings.map((f) => `${f.code} (${f.detail})`).join('; ')}`,
        );
      }
    }

    if (finalNote !== null) {
      withNote += 1;
      if (finalNote.length > CONNECTION_NOTE_CHAR_LIMIT) noteOverCap += 1;
      else noteWithinCap += 1;
    }

    rows.push({
      organization_id: input.orgId,
      brand: input.brand,
      dedupe_key: key,
      linkedin_url: key,
      first_name: lead.first_name,
      last_name: lead.last_name,
      company_name: lead.company_name,
      job_title: lead.job_title,
      source_campaign_key: lead.campaign_key,
      message_text: finalMessage,
      message_origin: override ? 'recrafted' : 'mirror_original',
      followup_text: finalFollowup,
      connection_note: finalNote,
      state: 'needs_connection',
    });

    const name = `${lead.first_name ?? ''} ${lead.last_name ?? ''}`.trim() || '(no name)';
    const flat = finalMessage.replace(/\s+/g, ' ');
    // The note is printed IN FULL and unflattened while the message is
    // excerpted: whether a campaign's copy is genuinely an invite note — rather
    // than a follow-up reused in that slot — can only be judged by reading the
    // whole thing, and it is short enough to read.
    const noteLine =
      finalNote === null
        ? '    note: — (bare request)'
        : `    note (${finalNote.length}/${CONNECTION_NOTE_CHAR_LIMIT}` +
          `${finalNote.length > CONNECTION_NOTE_CHAR_LIMIT ? ' OVER' : ''}): ` +
          `${finalNote.replace(/\n/g, '\n      ')}`;
    const block =
      `${name} @ ${lead.company_name ?? '-'} [${lead.campaign_key ?? '-'}]\n    ${key}\n` +
      `${noteLine}\n    msg: ${flat.slice(0, 140)}${flat.length > 140 ? '…' : ''}`;

    if (samples.length < 10) samples.push(block);
    if (finalNote !== null && noteSamples.length < 10) noteSamples.push(block);
  }

  const rejected = Object.values(rejections).reduce((sum, n) => sum + n, 0);

  return {
    rows,
    stats: {
      mirrorRows: input.mirror.length,
      alreadyContacted,
      alreadyQueued,
      rejected,
      toInsert: rows.length,
      withNote,
      noteWithinCap,
      noteOverCap,
      notesRejectedByGate,
    },
    rejections,
    samples,
    noteSamples,
    noteGateFindings,
  };
};
